import React, { useEffect, useRef, useState } from "react";
import videojs from "video.js";
import "videojs-youtube";
import Player from "video.js/dist/types/player";
import "video.js/dist/video-js.css";
import "./videoViewer.css";

interface sourceProps {
  src: string;
  type?: string;
}

interface videoJsProps extends React.VideoHTMLAttributes<HTMLVideoElement> {
  options: {
    autoplay?: boolean;
    progress?: boolean;
    controls?: boolean;
    responsive?: boolean;
    fluid?: boolean;
    fill?: boolean;
    disablePictureInPicture?: boolean;
    muted?: boolean;
    playbackRates?: number[];
    poster?: string;
    liveui?: boolean;
    sources: sourceProps[];
    aspectRatio?: string;
    loop?: boolean;
  };
  contentId?: string;
  videoSrc?: string;
  completedTill?: number;
  progressStatus?: string;
  block?: boolean;
  onVideoComplete?: () => void;
  timelineValue?: string | null;
  getVideoFromStrapi?: () => void;
  saveProgress?: boolean;
  className?: string;
}

const initialOptions = {
  controls: true,
  fluid: true,
  responsive: true,
  fill: true
};

const VideoViewer = ({
  options,
  contentId,
  videoSrc,
  completedTill,
  block = false,
  onVideoComplete,
  timelineValue,
  getVideoFromStrapi,
  className,
  saveProgress = true
}: videoJsProps): JSX.Element => {
  const videoRef = useRef<HTMLDivElement | null>(null);
  const playerRef = useRef<Player | null>(null);

  const lastTimeRef = useRef<number>(0);
  const maxWatchedRef = useRef<number>(completedTill ?? 0);

  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [isPlaying, setIsPlaying] = useState(false);

  const safePlay = (player: Player) => {
    const p = player.play();
    if (p !== undefined) p.catch(() => {});
  };

  useEffect(() => {
    if (playerRef.current) return;
    let player: Player;

    if (!playerRef.current) {
      const videoElement = document.createElement("video-js");

      videoElement.setAttribute("controlsList", "nodownload");
      videoElement.setAttribute("controls", "true");
      videoElement.classList.add("vjs-big-play-centered");
      if (videoRef.current && !videoRef.current.querySelector("video-js")) {
        videoRef.current.appendChild(videoElement);
      }

      const youtube = String(options.sources[0]).includes("youtube");

      player = playerRef.current = videojs(videoElement, {
        ...(youtube && { techOrder: ["youtube"] }),
        ...options,
        ...initialOptions,
        playbackRates: [0.5, 1, 1.5, 2]
      });

      const formatTime = (t: number) => {
        if (!t || isNaN(t) || !isFinite(t)) return "0:00";

        const m = Math.floor(t / 60);
        const s = Math.floor(t % 60)
          .toString()
          .padStart(2, "0");

        return `${m}:${s}`;
      };

      const updateTimeUI = () => {
        const current = player.currentTime() ?? 0;
        const duration = player.duration() ?? 0;

        const el = player.el().querySelector<HTMLElement>(".vjs-time-control");

        if (el) {
          el.textContent = `${formatTime(current)} / ${formatTime(duration)}`;
        }
      };

      player.on("timeupdate", updateTimeUI);
      player.on("loadedmetadata", updateTimeUI);

      player.on("play", () => setIsPlaying(true));
      player.on("pause", () => setIsPlaying(false));
    } else {
      player = playerRef.current;
      player.autoplay(options.autoplay);
      player.src(options.sources);
    }

    let initialLoad = true;

    player.on("timeupdate", () => {
      setTimeout(() => {
        if (initialLoad && completedTill) {
          lastTimeRef.current = completedTill;

          maxWatchedRef.current = Math.max(
            maxWatchedRef.current,
            completedTill
          );

          if (block) player.currentTime(completedTill + 0.1);
        } else {
          const ct = player.currentTime() ?? 0;

          if (ct > 0) {
            lastTimeRef.current = ct;
            maxWatchedRef.current = Math.max(maxWatchedRef.current, ct);
          }
        }

        initialLoad = false;
      }, 0);
    });

    const scheduleRefresh = () => {
      clearRefreshTimer();

      refreshTimerRef.current = setTimeout(() => {
        refreshSignedUrl();
        scheduleRefresh();
      }, 60000);
    };

    const clearRefreshTimer = () => {
      if (refreshTimerRef.current !== null) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };

    const refreshSignedUrl = () => {
      lastTimeRef.current = playerRef.current?.currentTime() ?? 0;
      getVideoFromStrapi?.();
    };

    scheduleRefresh();

    const btn = document.querySelector(
      ".custom-center-button"
    ) as HTMLElement | null;

    const showBtn = () => {
      if (btn) {
        btn.style.opacity = "1";
        btn.style.pointerEvents = "auto";
      }
    };

    let hideTimer: ReturnType<typeof setTimeout>;

    const resetTimer = () => {
      clearTimeout(hideTimer);
      showBtn();

      hideTimer = setTimeout(() => {
        if (btn) {
          btn.style.opacity = "0";
          btn.style.pointerEvents = "none";
        }
      }, 2000);
    };

    player.on("mousemove", resetTimer);
    player.on("touchstart", resetTimer);
    player.on("play", resetTimer);
    player.on("pause", showBtn);

    player.on("seeking", () => {
      const currentTime = player.currentTime() ?? 0;

      if (currentTime > lastTimeRef.current && block) {
        player.currentTime(lastTimeRef.current);
      }
    });

    player.on("loadedmetadata", () => {
      if (completedTill) {
        lastTimeRef.current = completedTill;
        player.currentTime(completedTill);
      }
    });

    player.on("ended", () => {
      if (saveProgress && onVideoComplete) {
        onVideoComplete();
      }
    });

    return () => {
      playerRef.current?.dispose();
      playerRef.current = null;

      if (videoRef.current) {
        const videoEl = videoRef.current.querySelector("video-js");
        if (videoEl) videoEl.remove();
      }

      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const player = playerRef.current;

    if (!player || !videoSrc) return;

    if (player.currentSrc() === videoSrc) return;

    const currentTime = lastTimeRef.current || (player.currentTime() ?? 0);

    const isPaused = player.paused();
    const rate = player.playbackRate();

    player.src({ src: videoSrc, type: "video/mp4" });

    player.one("loadedmetadata", () => {
      player.currentTime(completedTill ?? currentTime);
      player.playbackRate(rate);

      if (!isPaused) safePlay(player);
    });
  }, [videoSrc]);

  const hmsToSeconds = (hms: string) => {
    const [h, m, s] = hms.split(":").map(Number);
    return h * 3600 + m * 60 + s;
  };

  useEffect(() => {
    if (!timelineValue || !playerRef.current) return;

    const seconds = hmsToSeconds(timelineValue);

    playerRef.current.pause();
    playerRef.current.currentTime(seconds);
  }, [timelineValue]);

  return (
    <div
      data-vjs-player
      id="videoViewer"
      className={className ? `h-full w-full ${className}` : ""}
    >
      <div
        onContextMenu={(e: any) => e.preventDefault()}
        style={{ userSelect: "none" }}
        ref={videoRef}
        id="videoPlayer"
        className="h-full w-full"
      >
        <div
          className="custom-center-button"
          onClick={(e: any) => {
            e.stopPropagation();

            const player = playerRef.current;
            if (!player) return;

            player.paused() ? safePlay(player) : player.pause();
          }}
        >
          {isPlaying ? "⏸" : "▶"}
        </div>
      </div>
    </div>
  );
};

export default VideoViewer;
